import { colors } from '@mui/material';
import { filter } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceArea,
  Bar,
  Cell,
  ReferenceLine,
  Label,
  Tooltip,
} from 'recharts';
import { useData } from '../../contexts';

export const BarChartDeltaHighlightAndZoomHorizontal = (props: any) => {
  const {
    pageId,
    xAxisDataKey,
    yAxisDataKey,
    incomingData,
    resetZoom,
    setResetZoom,
    zoomGraph,
    setZoomGraph,
    setIsZoomedInsights,
    setDataTable,
    zoomFilters,
    setZoomFilters,
    colors,
    locale,
    CustomTooltip,
    highlightedRow,
    setHighlightedRow,
    tableHighlightedRow,
    setTableHighlightedRow,
  } = props;

  const { fillColorHighlight } = useData();

  const [dataGraph, setDataGraph] = useState(incomingData) as any[];
  const [valMax, setValMax] = useState(0);

  const zoom = () => {
    let { refAreaLeft, refAreaRight } = zoomGraph;
    const data = structuredClone(incomingData);

    if (
      refAreaLeft === refAreaRight ||
      refAreaRight === '' ||
      refAreaLeft == false ||
      refAreaRight == false
    ) {
      setZoomGraph((prev: any) => ({
        ...prev,
        refAreaLeft: '',
        refAreaRight: '',
      }));
      return;
    }

    // xAxis domain
    if (refAreaLeft && refAreaRight && refAreaLeft > refAreaRight)
      [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft];

    if (refAreaLeft && refAreaRight) {
      const newData = data.filter((d: any) => {
        return d.displayOrder >= refAreaLeft && d.displayOrder <= refAreaRight;
      });

      let filteredData = newData;

      let unique = [...new Set(filteredData.map((d: any) => d.id))];
      setZoomFilters((prev: any) => ({ ...prev, [pageId]: unique }));
      setIsZoomedInsights(true);

      setZoomGraph(
        (prev: any) =>
          ({
            ...prev,
            refAreaLeft: '',
            refAreaRight: '',
            left: refAreaLeft,
            right: refAreaRight,
            bottom: 0,
            top: '',
          } as any),
      );
    }
  };

  useEffect(() => {
    if (resetZoom === true) {
      setDataGraph(incomingData);
      setResetZoom(false);
    }
  }, [resetZoom]);

  useEffect(() => {
    let filteredData = structuredClone(incomingData);

    for (const property in zoomFilters) {
      filteredData = filteredData.filter((d: any) => {
        return zoomFilters[property].includes(d.id);
      });
    }

    let tmpData = structuredClone(filteredData).sort(
      (a: any, b: any) =>
        // a.indicator.localeCompare(b.indicator),
        b[xAxisDataKey] - a[xAxisDataKey],
    );

    const max = Math.max(...tmpData.map((o: any) => Math.abs(o[xAxisDataKey])));
    const min = Math.min(...tmpData.map((o: any) => Math.abs(o[xAxisDataKey])));

    const absMax = Math.max(min, max);

    const dataGraphWithoutEmpties = filteredData
      .filter((d: any) => {
        return d[xAxisDataKey] !== null;
      })
      .map((d: any) => {
        return d;
      });

    setValMax(absMax);

    setDataGraph(dataGraphWithoutEmpties);
    setDataTable(tmpData);
  }, [zoomFilters, incomingData]);

  const { refAreaLeft, refAreaRight } = zoomGraph;

  const handleMouseEnter = (event) => {
    // Access the data point ID from the event object
    const id = event.target.id;
    setTableHighlightedRow(id);
  };

  const handleMouseOut = (event) => {
    setTableHighlightedRow(null);
  };

  const handleTooltipClose = () => {
    setHighlightedRow(null);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={dataGraph}
        layout="horizontal"
        margin={{
          top: 16,
          right: 16,
          left: 0,
          bottom: 16,
        }}
        onMouseDown={(e: any) => {
          setZoomGraph((prev: any) => ({
            ...prev,
            refAreaLeft: e !== null && e.activeLabel,
          }));
        }}
        onMouseMove={(e: any) => {
          setZoomGraph((prev: any) => ({
            ...prev,
            refAreaRight: e !== null && e.activeLabel,
          }));
        }}
        onMouseUp={() => zoom()}
        onMouseLeave={(e: any) => {
          setZoomGraph((prev: any) => ({
            ...prev,
            refAreaLeft: null,
            refAreaRight: null,
          }));
        }}
      >
        {refAreaLeft && refAreaRight ? (
          <ReferenceArea
            // y1={-1}
            // y2={1}
            y1={-valMax}
            y2={valMax}
            x1={refAreaLeft > refAreaRight ? refAreaRight : refAreaLeft}
            x2={refAreaRight > refAreaLeft ? refAreaRight : refAreaLeft}
            fillOpacity={1}
            fill={'#ddd'}
          />
        ) : null}

        <ReferenceLine
          y={0}
          stroke="#666"
          label={<Label value="0" position="left" />}
        />

        <Tooltip
          allowEscapeViewBox={{ x: false, y: true }}
          wrapperStyle={{ zIndex: 10 }}
          content={<CustomTooltip />}
          cursor={{
            fill: '#ddd',
            stroke: '#666',
            strokeWidth: 2,
          }}
        />

        {/* <CartesianGrid strokeDasharray="3 3" /> */}

        <XAxis
          type="category"
          dataKey={yAxisDataKey}
          axisLine={true}
          width={0}
          tickLine={true}
          hide={true}
          interval={0}
        />

        <YAxis
          dataKey={xAxisDataKey}
          type="number"
          tick={{ fontSize: '14px' }}
          domain={[-valMax, valMax]}
          // domain={[-1, 1]}
          allowDataOverflow
          ticks={[-valMax, valMax]}
          tickFormatter={(tick) => {
            let formattedTick = locale.format(',.0%')(tick);
            let sign = tick > 0 ? '+' : '';
            return sign + formattedTick;
          }}
        />

        <Bar dataKey={xAxisDataKey} stackId="a" isAnimationActive={false}>
          {dataGraph.map((d: any, i: number) => {
            let color = d[xAxisDataKey] < 0 ? colors[1] : colors[0];
            color =
              d?.id === highlightedRow ||
              parseInt(d?.id) === parseInt(tableHighlightedRow)
                ? fillColorHighlight
                : color;
            const backgroundColor = d?.id === highlightedRow ? 'blue' : null;
            return (
              <Cell
                key={i}
                fill={color}
                stroke={d?.id === highlightedRow ? 'black' : null}
                strokeWidth={1}
                strokeOpacity={1}
                // onMouseEnter={handleMouseEnter}
                // onMouseOut={handleMouseOut}
              ></Cell>
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
